import { FulfillmentCenter } from 'src/features/types'
import { LocalPrinterData } from '../../PrintersManagementTypes'
import { useForm } from 'react-hook-form'
import * as S from './ModalEditPrinterSettings.styles'
import {
  Checkbox, FormControlLabel, FormGroup, Typography,
} from '@mui/material'
import {
  useEffect, useMemo, useState,
} from 'react'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import { actions } from '../../PrintersManagementState'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

interface ModalEditPrinterSettingsProps {
  show: boolean
  onClose: () => void
  localPrinter: LocalPrinterData
  fulfillmentCenters: FulfillmentCenter[]
}

interface FormValues {
  fulfillmentCenterId: number
  kdabraUrl: string
  deviceName: string
}

export const ModalEditPrinterSettings = ({
  show,
  onClose,
  localPrinter,
  fulfillmentCenters,
}: ModalEditPrinterSettingsProps) => {
  const {
    register, handleSubmit, setValue, reset, formState: { errors },
  } = useForm<FormValues>()

  const dispatch = useDispatch()

  const DEFAULT_KDABRA_URL = process.env.REACT_APP_URL_API || ''

  const [preventDoubleClick, setPreventDoubleClick] = useState(false)
  const [printerServerChecked, setPrinterServerChecked] = useState(localPrinter.printerServer)

  const fulfillmentCenterOptions = useMemo(() => fulfillmentCenters.map((fulfillmentCenter) => ({
    id: fulfillmentCenter.id,
    description: fulfillmentCenter.name,
  })), [fulfillmentCenters])

  useEffect(() => {
    reset()

    if (localPrinter) {
      setValue('kdabraUrl', localPrinter.kdabraUrl || DEFAULT_KDABRA_URL)
      setValue('deviceName', localPrinter.deviceName || '')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const onSubmit = (formData: FormValues) => {
    if (preventDoubleClick) {
      return
    }

    setPreventDoubleClick(true)

    const onSuccess = () => {
      dispatch(actions.getLocalPrinter())
      toast.success('Configurações salvas com sucesso!')
      setPreventDoubleClick(false)
      onClose()
    }

    dispatch(actions.saveLocalPrinterSettings({
      ...formData,
      printerServer: printerServerChecked,
      onSuccess,
      onError: () => setPreventDoubleClick(false),
    }))
  }

  return (
    <ConfirmOrRejectModal
      show={show}
      title='CONFIGURAÇÃO DE IMPRESSORA LOCAL'
      onConfirm={handleSubmit(onSubmit)}
      onReject={onClose}
      onClose={onClose}
      customizableDescription={(
        <S.Form>
          <S.CustomTextField
            name='macAddress'
            id='macAddress'
            label='Endereço MAC'
            type='text'
            InputLabelProps={{ shrink: true }}
            inputProps={{
              readOnly: true,
              'data-testid': 'modal-mac-address-test-id',
            }}
            value={localPrinter.macAddress}
            fullWidth
          />
          <S.CustomTextField
            name='localIp'
            id='localIp'
            label='Endereço IP Local'
            type='text'
            InputLabelProps={{ shrink: true }}
            inputProps={{
              readOnly: true,
              'data-testid': 'modal-local-ip-test-id',
            }}
            value={localPrinter.localIp}
            fullWidth
          />
          <S.CustomSelect
            name='fulfillmentCenterId'
            inputProps={{
              ...register(
                'fulfillmentCenterId',
                { required: 'Campo obrigatório' },
              ),
              'data-testid': 'modal-fulfillment-center-select-test-id',
            }}
            error={!!errors.fulfillmentCenterId}
            helperText={errors.fulfillmentCenterId?.message}
            autoComplete='off'
            id='fulfillmentCenterId'
            label='Selecione um FC'
            variant='outlined'
            options={fulfillmentCenterOptions}
            optionNone={false}
            type='text'
            autoFocus
            maxHeight='600px'
            dataTestId='modal-fulfillment-center-select-test-id'
            fullWidth
          />
          <S.CustomTextField
            name='kdabraUrl'
            id='kdabraUrl'
            label='Kdabra URL'
            type='text'
            inputProps={{
              ...register(
                'kdabraUrl',
                { required: 'Campo obrigatório' },
              ),
              'data-testid': 'modal-kdabra-url-test-id',
            }}
            error={!!errors.kdabraUrl}
            helperText={errors.kdabraUrl?.message}
            fullWidth
          />
          <S.CustomTextField
            name='deviceName'
            id='deviceName'
            label='Nome do Dispositivo'
            type='text'
            inputProps={{
              ...register(
                'deviceName',
                { required: 'Campo obrigatório' },
              ),
              'data-testid': 'modal-device-name-test-id',
            }}
            error={!!errors.deviceName}
            helperText={errors.deviceName?.message}
            fullWidth
          />
          <FormGroup
            sx={{
              marginTop: '-1.2rem',
              width: '100%',
              paddingTop: '.9rem',
            }}
          >
            <FormControlLabel
              control={(
                <Checkbox
                  name='printerServer'
                  id='printerServer'
                  onChange={() => setPrinterServerChecked(!printerServerChecked)}
                  inputProps={{
                    'aria-label': 'printer-server',
                    'data-testid': 'modal-printer-server-test-id',
                  } as React.InputHTMLAttributes<HTMLInputElement>}
                  checked={printerServerChecked}
                />
              )}
              label={(
                <Typography
                  sx={{
                    width: '100%',
                    display: 'flex',
                  }}
                  variant='body1'
                >
                  Habilitar servidor de impressão?
                </Typography>
              )}
            />
          </FormGroup>
        </S.Form>
      )}
      dataTestId='modal-edit-printer-settings-test-id'
      confirmButtonText='Salvar'
      rejectButtonText='Cancelar'
      size='lg'
    />
  )
}
